import React from 'react';
import moment from 'moment';

// Plain-language, step by step explanation of how a GO GA practice's pay was calculated.
// `narrative` is built by buildGoGaNarrative (api/helpers/doctorPayHelper.js).

const money = (n) => {
    const value = Number(n) || 0;
    const text = Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${value < 0 ? '-' : ''}$${text}`;
};
const signed = (n) => `${(Number(n) || 0) >= 0 ? '+' : '-'}${money(Math.abs(n))}`;
const optionLabel = (winner) => winner === 'A' ? 'A (percentage)' : 'B (daily rate)';

const cell = { padding: '3px 8px', textAlign: 'right', whiteSpace: 'nowrap' };
const labelCell = { ...cell, textAlign: 'left' };
const head = { ...cell, fontWeight: 'bold', borderBottom: '1px solid #ccc' };
const heading = { fontWeight: 'bold', margin: '14px 0 4px' };

const componentRows = [
    ['Billed for his work', 'production'],
    ['Adjustments', 'adjustments'],
    ['Write-offs (taken off)', 'writeOffs', -1],
    ['Billable notes', 'notes']
];

const BcareTable = ({ narrative }) => {
    const { ga, nonGa } = narrative;
    const total = (bucket, key, sign = 1) => sign * ((bucket.current[key] || 0) + (bucket.earlier[key] || 0));
    const hasOther = !!nonGa.otherBcare;
    const hasHyg = !!(ga.hygienistBcare || nonGa.hygienistBcare);
    return (
        <table style={{ borderCollapse: 'collapse', fontSize: '0.9em' }}>
            <thead>
                <tr>
                    <th style={labelCell} />
                    <th style={head} colSpan={3}>GA days</th>
                    <th style={head} colSpan={3}>Non-GA days</th>
                </tr>
                <tr>
                    <th style={labelCell} />
                    {['This month', 'Earlier months', 'Total', 'This month', 'Earlier months', 'Total'].map((h, i) => <th key={i} style={head}>{h}</th>)}
                </tr>
            </thead>
            <tbody>
                {componentRows.map(([label, key, sign = 1]) => (
                    <tr key={key}>
                        <td style={labelCell}>{label}</td>
                        <td style={cell}>{money(sign * ga.current[key])}</td>
                        <td style={cell}>{money(sign * ga.earlier[key])}</td>
                        <td style={cell}>{money(total(ga, key, sign))}</td>
                        <td style={cell}>{money(sign * nonGa.current[key])}</td>
                        <td style={cell}>{money(sign * nonGa.earlier[key])}</td>
                        <td style={cell}>{money(total(nonGa, key, sign))}</td>
                    </tr>
                ))}
                {hasHyg && (
                    <tr>
                        <td style={labelCell}>Hygienist care (credited to the doctor)</td>
                        <td style={cell}>{money(ga.hygienistBcare)}</td><td style={cell}>{money(0)}</td><td style={cell}>{money(ga.hygienistBcare)}</td>
                        <td style={cell}>{money(nonGa.hygienistBcare)}</td><td style={cell}>{money(0)}</td><td style={cell}>{money(nonGa.hygienistBcare)}</td>
                    </tr>
                )}
                {hasOther && (
                    <tr>
                        <td style={labelCell}>Other (e.g. hygienist-related)</td>
                        <td style={cell}>{money(0)}</td><td style={cell}>{money(0)}</td><td style={cell}>{money(0)}</td>
                        <td style={cell}>{money(nonGa.otherBcare)}</td><td style={cell}>{money(0)}</td><td style={cell}>{money(nonGa.otherBcare)}</td>
                    </tr>
                )}
                <tr style={{ fontWeight: 'bold', borderTop: '1px solid #ccc' }}>
                    <td style={labelCell}>bCare</td>
                    <td style={cell}>{money(ga.bCareCurrent)}</td>
                    <td style={cell}>{money(ga.bCareEarlier)}</td>
                    <td style={cell}>{money(ga.bCare)}</td>
                    <td style={cell}>{money(nonGa.bCareCurrent)}</td>
                    <td style={cell}>{money(nonGa.bCareEarlier)}</td>
                    <td style={cell}>{money(nonGa.bCare)}</td>
                </tr>
            </tbody>
        </table>
    );
};

const impactSentence = (bucket, payOverBase) => {
    const { label, bCareEarlier, thisMonthOnly, winner, pay, difference } = bucket;
    const share = `${payOverBase}%`;
    if (!bCareEarlier) return null;
    const direction = bCareEarlier > 0 ? 'added' : 'took off';
    const amount = money(Math.abs(bCareEarlier));
    if (thisMonthOnly.winner === 'A' && winner === 'A') {
        return `${label}: earlier items ${direction} ${amount} of bCare. The percentage was the winning option either way, so every dollar was paid at ${share}: ${signed(bCareEarlier)} × ${share} = ${signed(difference)}.`;
    }
    if (thisMonthOnly.winner === 'B' && winner === 'B') {
        return `${label}: earlier items ${direction} ${amount} of bCare, but the daily rate was already the higher option either way, so his pay was not affected.`;
    }
    const percentageEffect = bCareEarlier * payOverBase / 100;
    if (thisMonthOnly.winner === 'A' && winner === 'B') {
        return `${label}: earlier items ${direction} ${amount} of bCare. On the percentage alone that would have changed his pay by ${signed(percentageEffect)}, but the guaranteed daily rate took over and protected him, so his pay changed by only ${signed(difference)}.`;
    }
    return `${label}: earlier items ${direction} ${amount} of bCare. Without them the daily rate would have been paid; with them the percentage now wins, so his pay changed by ${signed(difference)}.`;
};

const GoGaNarrative = ({ narrative, practiceName, doctorName }) => {
    if (!narrative) return null;
    const { ga, nonGa, payOverBase, earlierItems = [], hasBreakdown } = narrative;
    const buckets = [ga, nonGa].filter(b => b.days > 0 || b.bCare !== 0);
    // Each bucket's this-month + earlier-month parts must add back up to the bCare that was paid on.
    const gapToReport = [ga, nonGa].some(b => Math.abs((b.bCareCurrent + b.bCareEarlier) - b.bCare) > 0.01);
    const sentences = hasBreakdown ? [ga, nonGa].map(b => impactSentence(b, payOverBase)).filter(Boolean) : [];

    return (
        <div style={{ fontSize: '0.9em', lineHeight: 1.5, color: '#333' }}>
            <div style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
                HOW {doctorName ? `${doctorName}'S ` : 'THE '}PAY WAS CALCULATED{practiceName ? ` · ${practiceName}` : ''}
            </div>
            <div style={{ marginTop: 4 }}>
                <b>The rule:</b> for GA days and Non-GA days separately, the provider is paid the higher of
                <b> A)</b> {payOverBase}% of what the practice earned on those days (bCare), or
                <b> B)</b> a guaranteed daily rate.
            </div>

            <div style={heading}>1 · Days worked</div>
            <div>{ga.days + nonGa.days} in total: {ga.days} GA ({money(ga.dailyRate)} per day) and {nonGa.days} Non-GA ({money(nonGa.dailyRate)} per day).</div>

            <div style={heading}>2 · What the practice earned (bCare)</div>
            {hasBreakdown ? <BcareTable narrative={narrative} /> : (
                <div>GA bCare {money(ga.bCare)} · Non-GA bCare {money(nonGa.bCare)}</div>
            )}
            {hasBreakdown && narrative.prodAdjWo !== 100 && (
                <div style={{ color: '#666', marginTop: 2 }}>
                    bCare = {narrative.prodAdjWo}% of (billed − write-offs + adjustments) + billable notes. Lines above are before the {narrative.prodAdjWo}% is applied.
                </div>
            )}
            {earlierItems.length > 0 && (
                <div style={{ marginTop: 6 }}>
                    <div>Earlier-month items, placed by what he was scheduled as on the original day:</div>
                    {earlierItems.map((item, i) => (
                        <div key={i} style={{ marginLeft: 12 }}>
                            {moment(item.date).format('D MMM YYYY')} · {item.type} {signed(item.amount)} → {item.bucket}
                        </div>
                    ))}
                </div>
            )}

            <div style={heading}>3 · Which option paid more</div>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.95em' }}>
                <thead>
                    <tr>
                        <th style={labelCell} />
                        <th style={head}>A) {payOverBase}% of bCare</th>
                        <th style={head}>B) Daily rate</th>
                        <th style={head}>Paid</th>
                    </tr>
                </thead>
                <tbody>
                    {buckets.map(b => (
                        <tr key={b.label}>
                            <td style={labelCell}>{b.label}</td>
                            <td style={cell}>{payOverBase}% × {money(b.bCare)} = {money(b.optionA)}</td>
                            <td style={cell}>{b.days} × {money(b.dailyRate)} = {money(b.optionB)}</td>
                            <td style={{ ...cell, fontWeight: 'bold' }}>{optionLabel(b.winner)} → {money(b.pay)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div style={heading}>4 · Total</div>
            <div>
                {buckets.map(b => money(b.pay)).join(' + ')} = <b>{money(narrative.totalPay)}</b>
            </div>
            {hasBreakdown && (
                <div style={{ color: gapToReport ? '#c0392b' : '#28a745' }}>
                    {gapToReport ? '⚠' : '✔'} Check: GA {money(ga.bCare)} + Non-GA {money(nonGa.bCare)} = {money(ga.bCare + nonGa.bCare)}
                    {gapToReport ? ' - the parts above do not add up to the bCare used for pay.' : ' = the practice bCare on the report.'}
                </div>
            )}

            {hasBreakdown && (
                <>
                    <div style={heading}>What the earlier-month items did</div>
                    {earlierItems.length === 0 && !ga.bCareEarlier && !nonGa.bCareEarlier ? (
                        <div>No earlier-month items this period.</div>
                    ) : (
                        <>
                            <table style={{ borderCollapse: 'collapse', fontSize: '0.95em' }}>
                                <thead>
                                    <tr>
                                        <th style={labelCell} />
                                        <th style={head}>This month only</th>
                                        <th style={head}>With earlier items</th>
                                        <th style={head}>Difference</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {buckets.map(b => (
                                        <tr key={b.label}>
                                            <td style={labelCell}>{b.label} pay</td>
                                            <td style={cell}>{money(b.thisMonthOnly.pay)} ({b.thisMonthOnly.winner})</td>
                                            <td style={cell}>{money(b.pay)} ({b.winner})</td>
                                            <td style={cell}>{signed(b.difference)}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ fontWeight: 'bold', borderTop: '1px solid #ccc' }}>
                                        <td style={labelCell}>Total</td>
                                        <td style={cell}>{money(narrative.totalThisMonthOnly)}</td>
                                        <td style={cell}>{money(narrative.totalPay)}</td>
                                        <td style={cell}>{signed(narrative.totalDifference)}</td>
                                    </tr>
                                </tbody>
                            </table>
                            {sentences.map((text, i) => <div key={i} style={{ marginTop: 4 }}>• {text}</div>)}
                            <div style={{ marginTop: 4 }}>
                                • Net: earlier-month items {narrative.totalDifference === 0 ? 'did not change' : narrative.totalDifference > 0 ? 'raised' : 'lowered'} his pay
                                {narrative.totalDifference === 0 ? '' : ` by ${money(Math.abs(narrative.totalDifference))}`} this month.
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default GoGaNarrative;
